import React, { useState } from 'react';
import classNames from 'classnames';
import { ENTER } from 'common/dom_helpers/keycodes_deprecated';
import { handleKeyPress } from 'common/dom_helpers/keyPressHelpers';
import { I18n } from 'common/i18n';
import { ForgeIcon, ForgeIconButton, ForgeTooltip } from '@tylertech/forge-react';

export interface PagerProps {
  changePage?: (page: number) => void;
  currentPage?: number;
  resultCount?: number;
  resultsPerPage?: number;
  isDisabled?: boolean;
}

interface PagerLinkOptions {
  isNext: boolean;
  linkIsDisabled: boolean;
  linkTabIndex: number;
  onClick: (e: React.MouseEvent) => void;
  text: string;
}

const renderPagerLink = (options: PagerLinkOptions) => {
  return (
    <ForgeIconButton
      onClick={options.onClick}
      disabled={options.linkIsDisabled}
      tabIndex={options.linkTabIndex}
      aria-label={options.text}
    >
      <ForgeIcon name={options.isNext ? 'keyboard_arrow_right' : 'keyboard_arrow_left'} />
      <ForgeTooltip placement={'bottom'}>{options.text}</ForgeTooltip>
    </ForgeIconButton>
  );
};

export const Pager: React.FC<PagerProps> = ({
  changePage = () => {},
  currentPage = 1,
  resultCount = 0,
  resultsPerPage = 10,
  isDisabled = false,
}) => {
  const [pageIsInvalid, setPageIsInvalid] = useState(false);

  const lastPage = () => {
    return Math.ceil(resultCount / resultsPerPage);
  };

  const prevLinkClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (currentPage > 1) {
      changePage(currentPage - 1);
      setPageIsInvalid(false);
    }
  };

  const nextLinkClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (currentPage < lastPage()) {
      changePage(currentPage + 1);
      setPageIsInvalid(false);
    }
  };

  const lastLinkClick = (e: React.MouseEvent) => {
    e.preventDefault();
    changePage(lastPage());
  };

  const pageInputChange = (e: React.FocusEvent<HTMLInputElement>) => {
    e.stopPropagation();
    const newPage = parseInt(e.target.value, 10);
    if (newPage === currentPage && !pageIsInvalid) return;
    if (newPage >= 1 && newPage <= lastPage()) {
      changePage(newPage);
      setPageIsInvalid(false);
    } else {
      setPageIsInvalid(true);
    }
  };

  const pageInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    e.stopPropagation();
    if (e.keyCode === ENTER) {
      e.preventDefault();
      e.currentTarget.blur();
    }
  };

  if (!lastPage() || lastPage() <= 1) {
    return <div className="pager"></div>;
  }

  const prevLinkDisabled = currentPage === 1;
  const prevLink = renderPagerLink({
    isNext: false,
    linkIsDisabled: isDisabled || prevLinkDisabled,
    linkTabIndex: prevLinkDisabled ? -1 : 0,
    onClick: prevLinkClick,
    text: I18n.t('shared.pager.previous_page'),
  });

  const nextLinkDisabled = currentPage === lastPage();
  const nextLink = renderPagerLink({
    isNext: true,
    linkIsDisabled: isDisabled || nextLinkDisabled,
    linkTabIndex: nextLinkDisabled ? -1 : 0,
    onClick: nextLinkClick,
    text: I18n.t('shared.pager.next_page'),
  });

  const currentPageInputClasses = classNames('current-page-input', 'inline-block', {
    error: pageIsInvalid,
  });

  const errorAltText =
    pageIsInvalid &&
    I18n.t('shared.pager.invalid_page_error').format({
      first: 1,
      last: lastPage(),
    });

  const pageNumberText = `${I18n.t('shared.pager.page')} ${currentPage}`;

  const currentPageInput = (
    <div className={currentPageInputClasses}>
      <input
        key={currentPage}
        type="text"
        alt={errorAltText || undefined}
        aria-label={pageNumberText}
        defaultValue={currentPage}
        onBlur={pageInputChange}
        onKeyDown={pageInputKeyDown}
      />
      <span className="accessible">{pageNumberText}</span>
    </div>
  );

  const lastPageText = I18n.t('shared.pager.last_page');

  const lastPageLink = (
    <a
      href="#"
      className="forge-hyperlink"
      onClick={lastLinkClick}
      onKeyDown={handleKeyPress(lastLinkClick)}
      role="button"
      aria-label={lastPageText}
      title={lastPageText}
    >
      {lastPage()}
      <span className="accessible">{lastPageText}</span>
    </a>
  );

  return (
    <div className="pager">
      {prevLink}
      {currentPageInput}
      <span className="page-of">{` ${I18n.t('shared.pager.of')} `}</span>
      {lastPageLink}
      {nextLink}
    </div>
  );
};

export default Pager;
